import { useState, useEffect, useCallback } from 'react';
import { Download, Building2, FileText, Clock, TrendingUp, RefreshCw, Search, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getToken = () => localStorage.getItem('mosaic_session_token');

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const parseFilename = (filename) => {
  // MOSAIC_K_listening_student_ALL.pdf -> { grade: 'K', modality: 'Listening', type: 'Student', tier: 'ALL' }
  const m = filename.match(/MOSAIC_([^_]+(?:_\d+)?)_(\w+)_(\w+)_(\w+)\.pdf/);
  if (!m) return { grade: '?', modality: '?', type: '?', tier: '?' };
  const gradeMap = { K: 'K', '1': '1', '2': '2', '3_4': '3-4', '5_6': '5-6', '7_8': '7-8', '9_12': '9-12' };
  return {
    grade: gradeMap[m[1]] || m[1],
    modality: m[2].charAt(0).toUpperCase() + m[2].slice(1),
    type: m[3] === 'student' ? 'Student' : 'Teacher',
    tier: m[4],
  };
};

const MODALITY_COLORS = {
  Listening: 'bg-sky-100 text-sky-700',
  Reading: 'bg-emerald-100 text-emerald-700',
  Speaking: 'bg-amber-100 text-amber-700',
  Writing: 'bg-purple-100 text-purple-700',
};

const DownloadActivityTab = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('downloaded_at');
  const [sortDir, setSortDir] = useState('desc');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/blm/download-logs?limit=500`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setLogs(res.data.logs || []);
      setStats(res.data.stats || {});
    } catch (err) {
      console.error('Failed to fetch download logs:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const filtered = logs.filter((log) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (log.school_name || '').toLowerCase().includes(term) ||
      (log.email || '').toLowerCase().includes(term) ||
      (log.filename || '').toLowerCase().includes(term) ||
      (log.download_id || '').toLowerCase().includes(term)
    );
  });

  const sorted = [...filtered].sort((a, b) => {
    const av = a[sortField] || '';
    const bv = b[sortField] || '';
    return sortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
  });

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronDown size={12} className="opacity-30" />;
    return sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />;
  };

  // Aggregate stats
  const uniqueSchools = new Set(logs.map(l => l.school_name)).size;
  const uniqueUsers = new Set(logs.map(l => l.email)).size;
  const todayCount = logs.filter(l => {
    const d = new Date(l.downloaded_at);
    const today = new Date();
    return d.toDateString() === today.toDateString();
  }).length;

  // Modality breakdown
  const modalityBreakdown = {};
  logs.forEach(l => {
    const parsed = parseFilename(l.filename);
    modalityBreakdown[parsed.modality] = (modalityBreakdown[parsed.modality] || 0) + 1;
  });

  return (
    <div className="space-y-6" data-testid="download-activity-tab">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border-2 border-gray-100 p-4" data-testid="stat-total-downloads">
          <Download size={20} className="text-[#002FA7]" />
          <div className="text-3xl font-black mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {stats?.total_downloads ?? logs.length}
          </div>
          <div className="text-xs text-gray-500 font-medium">Total Downloads</div>
        </div>
        <div className="bg-white border-2 border-gray-100 p-4" data-testid="stat-unique-schools">
          <Building2 size={20} className="text-emerald-600" />
          <div className="text-3xl font-black mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {uniqueSchools}
          </div>
          <div className="text-xs text-gray-500 font-medium">Schools Downloading</div>
        </div>
        <div className="bg-white border-2 border-gray-100 p-4" data-testid="stat-unique-users">
          <TrendingUp size={20} className="text-amber-600" />
          <div className="text-3xl font-black mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {uniqueUsers}
          </div>
          <div className="text-xs text-gray-500 font-medium">Unique Users</div>
        </div>
        <div className="bg-white border-2 border-gray-100 p-4" data-testid="stat-today-downloads">
          <Clock size={20} className="text-purple-600" />
          <div className="text-3xl font-black mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {todayCount}
          </div>
          <div className="text-xs text-gray-500 font-medium">Today</div>
        </div>
      </div>

      {/* Top Schools + Modality Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Top Schools */}
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="top-schools-panel">
          <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
            <Building2 size={16} className="text-[#002FA7]" /> Top Schools by Downloads
          </h3>
          {(stats?.top_schools || []).length === 0 ? (
            <p className="text-xs text-gray-400">No download data yet</p>
          ) : (
            <div className="space-y-2">
              {(stats?.top_schools || []).map((s, i) => {
                const maxCount = stats?.top_schools?.[0]?.downloads || 1;
                const pct = Math.round((s.downloads / maxCount) * 100);
                return (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-xs font-bold text-gray-500 w-5">{i + 1}</span>
                    <div className="flex-1">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-xs font-medium text-gray-700 truncate max-w-[200px]">{s.school}</span>
                        <span className="text-xs font-bold text-[#002FA7]">{s.downloads}</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full">
                        <div className="h-1.5 bg-[#002FA7] rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Modality Breakdown */}
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="modality-breakdown-panel">
          <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
            <FileText size={16} className="text-amber-600" /> Downloads by Modality
          </h3>
          {Object.keys(modalityBreakdown).length === 0 ? (
            <p className="text-xs text-gray-400">No download data yet</p>
          ) : (
            <div className="space-y-3">
              {Object.entries(modalityBreakdown).sort((a, b) => b[1] - a[1]).map(([mod, count]) => {
                const total = logs.length || 1;
                const pct = Math.round((count / total) * 100);
                const colorClass = MODALITY_COLORS[mod] || 'bg-gray-100 text-gray-700';
                return (
                  <div key={mod}>
                    <div className="flex justify-between items-center mb-1">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${colorClass}`}>{mod}</span>
                      <span className="text-xs text-gray-500">{count} ({pct}%)</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full">
                      <div className={`h-2 rounded-full transition-all ${mod === 'Listening' ? 'bg-sky-400' : mod === 'Reading' ? 'bg-emerald-400' : mod === 'Speaking' ? 'bg-amber-400' : 'bg-purple-400'}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Search + Refresh */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by school, email, file, or tracking ID..."
            className="pl-9 text-sm"
            data-testid="download-search-input"
          />
        </div>
        <Button variant="outline" size="sm" onClick={fetchLogs} disabled={loading} data-testid="refresh-downloads-btn">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Refresh
        </Button>
        <span className="text-xs text-gray-400">{sorted.length} records</span>
      </div>

      {/* Download Log Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="download-log-table">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-3 py-2 font-semibold text-gray-600 cursor-pointer hover:text-gray-900" onClick={() => toggleSort('downloaded_at')}>
                  <span className="flex items-center gap-1">Date <SortIcon field="downloaded_at" /></span>
                </th>
                <th className="text-left px-3 py-2 font-semibold text-gray-600 cursor-pointer hover:text-gray-900" onClick={() => toggleSort('school_name')}>
                  <span className="flex items-center gap-1">School <SortIcon field="school_name" /></span>
                </th>
                <th className="text-left px-3 py-2 font-semibold text-gray-600 cursor-pointer hover:text-gray-900" onClick={() => toggleSort('email')}>
                  <span className="flex items-center gap-1">User <SortIcon field="email" /></span>
                </th>
                <th className="text-left px-3 py-2 font-semibold text-gray-600">File</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-600">Grade</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-600">Modality</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-600">Type</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-600">Tracking ID</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-12 text-gray-400">Loading...</td></tr>
              ) : sorted.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-12 text-gray-400">No downloads recorded yet</td></tr>
              ) : (
                sorted.map((log, i) => {
                  const parsed = parseFilename(log.filename);
                  const modColor = MODALITY_COLORS[parsed.modality] || 'bg-gray-100 text-gray-700';
                  return (
                    <tr key={i} className="border-b border-gray-100 hover:bg-blue-50/30 transition-colors" data-testid={`download-row-${i}`}>
                      <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{formatDate(log.downloaded_at)}</td>
                      <td className="px-3 py-2 font-medium text-gray-800 max-w-[180px] truncate">{log.school_name}</td>
                      <td className="px-3 py-2 text-gray-600 max-w-[160px] truncate">{log.email}</td>
                      <td className="px-3 py-2 text-gray-500 font-mono text-[10px] max-w-[200px] truncate">{log.filename}</td>
                      <td className="px-3 py-2"><span className="bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded text-[10px] font-bold">{parsed.grade}</span></td>
                      <td className="px-3 py-2"><span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${modColor}`}>{parsed.modality}</span></td>
                      <td className="px-3 py-2"><span className={`text-[10px] font-medium ${parsed.type === 'Student' ? 'text-blue-600' : 'text-orange-600'}`}>{parsed.type}</span></td>
                      <td className="px-3 py-2 font-mono text-[10px] text-gray-400">{log.download_id}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DownloadActivityTab;
